import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import {
  Activity, AlertTriangle, BarChart3, TrendingDown,
  Loader2, ShieldCheck, Layers, Gauge, RefreshCw, Package
} from "lucide-react";

const scoreColor = (score, max = 10) => {
  const pct = score / max;
  if (pct <= 0.3) return "text-emerald-500";
  if (pct <= 0.6) return "text-amber-500";
  return "text-red-500";
};

const itppColor = (score) => {
  if (score >= 80) return { text: "text-emerald-400", bg: "bg-emerald-500/20", border: "border-emerald-500/30" };
  if (score >= 60) return { text: "text-sky-400", bg: "bg-sky-500/20", border: "border-sky-500/30" };
  if (score >= 40) return { text: "text-amber-400", bg: "bg-amber-500/100/20", border: "border-amber-500/30" };
  return { text: "text-red-400", bg: "bg-red-500/100/20", border: "border-red-500/30" };
};

const ITPPPage = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/itpp/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch { toast.error("Erreur de chargement des indicateurs"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
        </div>
      </Layout>
    );
  }

  if (!data) return <Layout><div className="p-8 text-center text-slate-400">Aucune donnée disponible</div></Layout>;

  const { isc, ipr, ivp, itpp, expired_analysis, inconsistencies, order_recommendations } = data;
  const colors = itppColor(itpp.score);

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-6" data-testid="itpp-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-outfit font-bold text-white flex items-center gap-3">
              <Gauge className="w-8 h-8 text-sky-400" />
              Indicateurs Prédictifs ITPP
            </h1>
            <p className="text-slate-400 mt-1">Score global de performance pharmaceutique</p>
          </div>
          <Button onClick={load} variant="outline" className="border-white/10 text-slate-300 hover:bg-white/5">
            <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
          </Button>
        </div>

        {/* ITPP Score */}
        <Card className={`glass-card border ${colors.border}`}>
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className={`w-32 h-32 rounded-full ${colors.bg} flex items-center justify-center border-2 ${colors.border}`}>
                <div className="text-center">
                  <p className={`text-4xl font-outfit font-bold ${colors.text}`}>{itpp.score}</p>
                  <p className="text-slate-500 text-xs">/100</p>
                </div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <p className={`text-xl font-bold ${colors.text}`}>{itpp.label}</p>
                <p className="text-slate-400 text-sm mt-1">
                  Indice de Tendance Prévisionnelle Pharmaceutique
                </p>
                <div className="mt-3">
                  <Progress value={itpp.score} className="h-3" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sub-Indicators */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* ISC */}
          <Card className="glass-card" data-testid="isc-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-white flex items-center gap-2">
                <Layers className="w-5 h-5 text-violet-400" /> ISC - Saturation Catégorie
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-end gap-2">
                <p className={`text-3xl font-bold ${scoreColor(isc.score)}`}>{isc.score}</p>
                <p className="text-slate-500 text-sm mb-1">/10</p>
              </div>
              <Progress value={isc.score * 10} className="h-2" />
              <p className="text-slate-400 text-xs">{isc.total_categories} catégories en stock</p>
              {isc.saturated_categories?.slice(0, 3).map((c, i) => (
                <div key={i} className="flex justify-between text-xs">
                  <span className="text-slate-300">{c.category}</span>
                  <span className="text-violet-400 font-medium">{c.share}%</span>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* IPR */}
          <Card className="glass-card" data-testid="ipr-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-white flex items-center gap-2">
                <Activity className="w-5 h-5 text-sky-400" /> IPR - Potentiel Rotation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-end gap-2">
                <p className={`text-3xl font-bold ${scoreColor(10 - ipr.score)}`}>{ipr.score}</p>
                <p className="text-slate-500 text-sm mb-1">/10</p>
              </div>
              <Progress value={ipr.score * 10} className="h-2" />
              <p className="text-slate-400 text-xs">{ipr.active_products} produits actifs</p>
              <p className="text-slate-400 text-xs">Taux de destruction: {ipr.destruction_rate}</p>
            </CardContent>
          </Card>

          {/* IVP */}
          <Card className="glass-card" data-testid="ivp-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" /> IVP - Volatilité Produit
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-end gap-2">
                <p className={`text-3xl font-bold ${scoreColor(ivp.score)}`}>{ivp.score}</p>
                <p className="text-slate-500 text-sm mb-1">/10</p>
              </div>
              <Progress value={ivp.score * 10} className="h-2" />
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-red-500/100/10 rounded p-1.5">
                  <p className="text-red-400 font-bold text-sm">{ivp.expired}</p>
                  <p className="text-[9px] text-slate-500">Périmés</p>
                </div>
                <div className="bg-amber-500/100/10 rounded p-1.5">
                  <p className="text-amber-400 font-bold text-sm">{ivp.expiring_3m}</p>
                  <p className="text-[9px] text-slate-500">{"<3 mois"}</p>
                </div>
                <div className="bg-sky-500/10 rounded p-1.5">
                  <p className="text-sky-400 font-bold text-sm">{ivp.expiring_6m}</p>
                  <p className="text-[9px] text-slate-500">{"<6 mois"}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Inconsistencies */}
        {inconsistencies.length > 0 && (
          <Card className="glass-card border border-red-500/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-red-400 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" /> Incohérences détectées
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {inconsistencies.map((inc, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-red-500/100/5 border border-red-500/10">
                  <ShieldCheck className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                  <p className="text-slate-300 text-sm">{inc.message}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Order Recommendations */}
        {order_recommendations.length > 0 && (
          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-white flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-emerald-400" /> Recommandations de commande
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {order_recommendations.map((rec, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-white/5">
                  <div className="flex items-center gap-3">
                    <Package className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-slate-200 text-sm font-medium">{rec.category}</p>
                      <p className="text-slate-500 text-xs">{rec.message}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-red-400 font-bold text-sm">-{rec.suggested_reduction_pct}%</p>
                    <p className="text-slate-500 text-[10px]">{rec.expired_value.toLocaleString()} F périmés</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Expired Analysis */}
        {expired_analysis.by_category.length > 0 && (
          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-red-400" /> Analyse des périmés par catégorie
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {expired_analysis.by_category.map((cat, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-slate-300 text-sm w-32 truncate">{cat.category}</span>
                    <div className="flex-1">
                      <div className="h-4 bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-red-500 to-amber-500 rounded-full"
                          style={{ width: `${Math.min((cat.value / (expired_analysis.total_expired_value || 1)) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-amber-400 font-mono text-sm">{cat.value.toLocaleString()} F</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 rounded-lg bg-red-500/100/10 border border-red-500/20 text-center">
                <p className="text-red-400 text-sm">Valeur totale périmée</p>
                <p className="text-2xl font-bold text-red-300">{expired_analysis.total_expired_value.toLocaleString()} FCFA</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default ITPPPage;
